import * as React from "react";
import styled from "styled-components/native";
import { Animated, Easing } from "react-native";

import {
  background,
  Flexible,
  Full,
  rounded,
  Space,
} from "../../styled-component";
import { Theme } from "../../styles";
import Layout from "./Layout";

import Button from "./PressableButton";
import { get } from "lodash";
import { LayoutRectangle } from "react-native";

const navButtonWidth = Theme.spacing * 12;

const ToggleInputContainer = styled(Layout.Box).attrs({
  background: Theme.colors.backgroundInfo,
  size: 1, // 0.5,
})`
  ${rounded};
  ${background};
`;

const ToggleInputButton = styled(Button).attrs({
  background: Theme.colors.transparent,
  borderColor: Theme.colors.transparent,
})`
  min-width: ${navButtonWidth}px;
`;

const ToggleInputIndicator = styled(Animated.View).attrs({
  background: Theme.colors.background,
})<{ height: number }>`
  ${background};
  ${rounded};

  height: ${(props) => props.height}px;
  position: absolute;
`;

export type ToggleInputProps = {
  value?: string;
  onChange?: (value: string) => void;
  options?: string[];
  renderLabel?: (option: string) => string;
  layout?: "column" | "row";
  disabled?: boolean;
} & Full &
  Flexible;

function ToggleInput({
  value,
  onChange,
  options,
  renderLabel,
  layout = "row",
  disabled,
  ...rest
}: ToggleInputProps) {
  const [measurements, setMeasurements] = React.useState<
    Record<string, LayoutRectangle>
  >({});
  const isRow = layout === "row";
  const Wrapper = React.useCallback(
    ({ children }: { children?: React.ReactNode }) =>
      isRow ? (
        <Layout.Row fullHeight>{children}</Layout.Row>
      ) : (
        <Layout.Column>{children}</Layout.Column>
      ),
    [isRow]
  );

  const indicatorLeft = React.useMemo(() => {
    return value
      ? get(measurements, [value, isRow ? "x" : "y"], Theme.spacing)
      : Theme.spacing;
  }, [value, measurements, isRow]);

  const buttonHeight = React.useMemo(() => {
    return value ? get(measurements, [value, "height"], 0) : Theme.spacing;
  }, [value, measurements]);

  const buttonWidth = React.useMemo(() => {
    return value ? get(measurements, [value, "width"], 0) : Theme.spacing;
  }, [value, measurements]);

  const { current: slideAnim } = React.useRef(
    new Animated.Value(indicatorLeft)
  );

  const { current: growAnim } = React.useRef(new Animated.Value(buttonWidth));

  React.useEffect(() => {
    const timing = Animated.timing(slideAnim, {
      toValue: indicatorLeft,
      duration: 66,
      easing: Easing.ease,
      useNativeDriver: false,
    });

    timing.start();

    return () => {
      timing.stop();
    };
  }, [slideAnim, indicatorLeft]);

  React.useEffect(() => {
    const timing = Animated.timing(growAnim, {
      toValue: isRow ? buttonWidth : buttonHeight,
      duration: 66,
      easing: Easing.ease,
      useNativeDriver: false,
    });

    timing.start();

    return () => {
      timing.stop();
    };
  }, [growAnim, buttonWidth, buttonHeight, isRow]);

  return (
    <ToggleInputContainer {...rest}>
      <Wrapper>
        {!!value && (
          <ToggleInputIndicator
            style={
              isRow
                ? { left: slideAnim, width: growAnim }
                : { top: slideAnim, width: buttonWidth }
            }
            height={buttonHeight}
          />
        )}

        {options?.map((option, i) => (
          <React.Fragment key={option}>
            {i > 0 && <Space />}

            <ToggleInputButton
              disabled={disabled}
              title={renderLabel?.(option) || option}
              onPress={() => onChange?.(option)}
              mode="text"
              onLayout={(event) => {
                const { layout } = event.nativeEvent || {};
                setMeasurements((prevMeasurements) => ({
                  ...prevMeasurements,
                  [option]: layout,
                }));
              }}
            />
          </React.Fragment>
        ))}
      </Wrapper>
    </ToggleInputContainer>
  );
}

export default ToggleInput;
